import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    computed,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';

import { OrderType, TotalsPrefix } from '@cds/betting-offer';
import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { CrmPromoIdsMap, EventModel, EventOption, EventOptionGroup, SortOrder } from '@frontend/sports/betting-offer/feature/model';
import { MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { ParticipantImageComponent, ParticipantImageService, ParticipantType } from '@frontend/sports/participant-image/feature';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Store } from '@ngrx/store';
import { RxFor } from '@rx-angular/template/for';
import { Subscription, map } from 'rxjs';

import { ISheetViewState, sheetViewStateSelector } from '../sheet-view-core/sheet-view.state';
import { SHEET_VIEW_OPEN } from '../sheet-view-core/sheet-view.token';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionAttributeNameStrategy } from './option-attribute-name.strategy';
import { OptionGroupTrackingService } from './option-group-tracking.service';
import { OptionGroup, OptionGroupService } from './option-group.service';
import { OptionNameStrategy } from './option-name.strategy';
import { OptionPanelBottomActionComponent, OptionPanelBottomActionState, ShowMoreData } from './option-panel-bottom-action.component';
import { OptionPickPlaceholderComponent } from './option-pick-placeholder.component';
import { OptionComponent } from './option.component';
import { loadPlayerStatisticsData } from './player-statistics/player-statistics.actions';
import { PlayerStatisticsComponent } from './player-statistics/player-statistics.component';
import { PlayerStatisticsStoreData } from './player-statistics/player-statistics.model';
import { PlayerStatisticsService } from './player-statistics/player-statistics.service';
import { selectDataByEventAndMarket } from './player-statistics/player-statistics.state';
import { PlayerStatsComponent } from './player-stats/player-stats.component';
import { SplitHeaderComponent } from './split-header/split-header.component';
import { TeamGroupingTabBarComponent } from './team-grouping/team-grouping-tab-bar.component';

export interface IPlayerPropsOptionLine {
    playerId: number | undefined;
    playerName: string;
    overOption?: EventOption;
    underOption?: EventOption;
    showStats?: boolean;
}

@Component({
    selector: 'ms-player-props-option-group',
    templateUrl: './player-props-option-group.component.html',
    styleUrl: './player-props-option-group.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: OptionNameStrategy,
            useClass: OptionAttributeNameStrategy,
        },
        ParticipantImageService,
        PlayerStatisticsService,
    ],
    imports: [
        RxFor,
        NgTemplateOutlet,
        MarketHelpComponent,
        TeamGroupingTabBarComponent,
        PlayerStatsComponent,
        OptionComponent,
        OptionPanelBottomActionComponent,
        SplitHeaderComponent,
        ParticipantImageComponent,
        OptionPickPlaceholderComponent,
        PlayerStatisticsComponent,
        IconCustomComponent,
    ],
})
export class PlayerPropsOptionGroupComponent implements OnInit, OnChanges {
    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));

    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() optionGroup: EventOptionGroup;
    @Input() showMarketHelp: boolean;
    @Input() crmPromoIdsMap: CrmPromoIdsMap;
    @Input() eventHelpText?: string;
    @Input() sourceOfPick: SourceOfPick;
    @Input() betDetails = '';
    @Input() optionsLimitOverride?: number;
    @Input() showPlayerStats = false;

    @Output() showAllRequested = new EventEmitter<void>();

    @ViewChild('optionGroupContainer') container: ElementRef<HTMLElement>;
    @ViewChild('splitHeaderComponent') splitHeaderComponent: SplitHeaderComponent;

    _optionsLimitOverride = signal<number | undefined>(undefined);
    optionLines = signal<IPlayerPropsOptionLine[]>([]);

    optionLines$ = toObservable(
        computed(() => {
            if (this.visibleOptionsLimit() == null) {
                return this.optionLines();
            }

            return this.optionLines().filter((_, i) => i < this.visibleOptionsLimit()!);
        }),
    );

    overHeader: string;
    underHeader: string;

    expanded = signal(false);
    showMoreLessButtonVisible = computed(() => this.optionLines().length > (this._optionsLimitOverride() ?? this.teamGroupingMaxNumberOfLines));
    visibleOptionsLimit = computed(() => {
        return this.isInSheetView || this.expanded() ? undefined : (this.optionsLimitOverride ?? this.teamGroupingMaxNumberOfLines);
    });

    visibleOptionGroups = computed(() => {
        let length = this.optionLines().length;
        if (this.visibleOptionsLimit() != null) {
            length = Math.min(this.visibleOptionsLimit()!, length);
        }

        return length;
    });

    @HostBinding('class') className = 'player-props-option-group';

    teamGroupingMaxNumberOfLines = this.marketGroupingConfig.teamGroupingMaxNumberOfLines;
    private _teamGroupingTabVisibilityThreshold = this.marketGroupingConfig.teamGroupingTabVisibilityThreshold;
    isTeamGroupingEnabled = false;
    controllerTabName: string;
    isShowHeadshotImage: boolean;
    participantType = ParticipantType;

    showMoreData: ShowMoreData;
    sourcesOfPick: SourceOfPick[];

    store = inject(Store);
    playerStatisticsService = inject(PlayerStatisticsService);
    isPlayerStatisticsEnabled = false;
    isSeasonStatisticsEnabled = signal(false);
    playerStatistics = signal<PlayerStatisticsStoreData>({
        isLoading: true,
        isError: false,
        data: undefined,
    });
    private dataSubscriptions: Map<string, Subscription> = new Map();
    private destroyRef = inject(DestroyRef);
    readonly seasonStatisticsMessage = this.playerStatisticsService.seasonStatisticsMessage;

    isSheetViewOpen = toSignal(this.store.select(sheetViewStateSelector).pipe(map((sheetViewState: ISheetViewState) => sheetViewState.isOpen)), {
        initialValue: false,
    });

    isStatisticsOpen = computed(() => {
        const statisticsOpenState: { [key: string]: boolean } = {};
        const optionLines = this.optionLines();
        optionLines.forEach((optionLine: IPlayerPropsOptionLine) => {
            const optionGroupId = (optionLine.overOption?.optionGroupId ?? optionLine.underOption?.optionGroupId)?.toString()!;
            statisticsOpenState[optionGroupId] = !!this.playerStatisticsService.getPlayerStatistics(
                this.playerStatistics()?.data,
                optionGroupId?.toString(),
            )?.isOpen;
        });
        return statisticsOpenState;
    });
    readonly playerStatisticsMessages = this.playerStatisticsService.playerStatisticsMessages;

    constructor(
        public sitecore: Sitecore,
        private marketGroupingConfig: MarketGroupingConfig,
        private optionGroupService: OptionGroupService,
        private optionGroupTrackingService: OptionGroupTrackingService,
        private changeDetectorRef: ChangeDetectorRef,
        private participantImageService: ParticipantImageService,
    ) {}

    ngOnInit(): void {
        this.controllerTabName =
            this.sitecore.teamGrouping[this.optionGroup.detailedGrouping.teamGrouping! as keyof typeof this.sitecore.teamGrouping];
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.overHeader = this.optionGroup.parameterTranslations?.columnOver ?? '';
        this.underHeader = this.optionGroup.parameterTranslations?.columnUnder ?? '';

        if (changes.optionsLimitOverride) {
            this._optionsLimitOverride.set(this.optionsLimitOverride);
        }

        if (changes.event || changes.optionGroup || changes.sourceOfPick) {
            if (changes.event?.previousValue !== changes.event?.currentValue) {
                this.fetchPlayerStatistics();
            }
            this.optionLines.set(this.getOptionLines(this.optionGroup));
            this.setProperties();
            this.sourcesOfPick = [];
            this.sourcesOfPick.push(changes.sourceOfPick?.currentValue);

            if (this.isTeamGroupingEnabled) {
                this.sourceOfPick = SourceOfPick.TeamGrouping;
                this.sourcesOfPick.push(SourceOfPick.TeamGrouping);
            }
            this.isShowHeadshotImage = this.participantImageService.showHeadshotImage(this.event.league.id, OptionGroupType.PlayerProps);
        }

        if (changes.event || changes.optionGroup || changes.optionsLimitOverride) {
            const totalCount = this.optionLines().length;
            this.showMoreData = {
                totalCount,
                initialVisibleCount: this.optionsLimitOverride ?? this.teamGroupingMaxNumberOfLines,
                pageSize: totalCount,
            };
        }
    }

    trackOptionLine(index: number, optionLine: IPlayerPropsOptionLine): string {
        return `${optionLine.overOption?.id}-${optionLine.underOption?.id}`;
    }

    trackGroup(index: number, group: OptionGroup): string {
        return group.attribute || '';
    }

    isMarketPromoted(optionGroupId: number, templateId: number): boolean {
        return !!this.crmPromoIdsMap.markets[optionGroupId] || (templateId != null && !!this.crmPromoIdsMap.templateIds[templateId]);
    }

    onShowMore({ state }: { state: OptionPanelBottomActionState; visibleCount: number }): void {
        this.expanded.set(state !== 'collapsed');

        this.optionGroupTrackingService.trackCollapseExpandClick(
            this.event.sport.name,
            this.expanded() ? 'expand' : 'collapse',
            this.event.participants,
            this.optionGroup.name,
            'players',
        );
    }

    getPlayers(optionGroup: IPlayerPropsOptionLine): number[] {
        const players: number[] = [];

        if (optionGroup.playerId) {
            players.push(optionGroup.playerId);
        }

        return players;
    }

    onTeamGroupingChange(teamGrouping: { optionGroup: EventOptionGroup; controllerTabName?: string }): void {
        this.optionLines.set(this.getOptionLines(teamGrouping.optionGroup));
        this.controllerTabName = teamGrouping.controllerTabName ?? this.controllerTabName;
        this.changeDetectorRef.detectChanges();
        this.splitHeaderComponent.scrollToTop();
    }

    getTracking(optionLine: IPlayerPropsOptionLine) {
        return {
            source: this.tracking,
            moduleType: this.trackingModuleType,
            controllerTabName: this.controllerTabName,
            playersName: optionLine.playerName,
        };
    }

    isPlayer(playerName: string): boolean {
        return this.playerStatisticsService.isPlayer(this.event.players, playerName);
    }

    toggleStatisticsOpenState(optionLine: IPlayerPropsOptionLine): void {
        const optionGroupId = (optionLine.overOption?.optionGroupId ?? optionLine.underOption?.optionGroupId)?.toString()!;
        const isStatisticsOpen = this.isStatisticsOpen()[optionGroupId];
        this.playerStatisticsService.openStatistics({
            eventId: this.event.id,
            marketFilter: this.optionGroup.originalId,
            playerName: optionLine.playerName,
            marketId: (optionLine.overOption?.optionGroupId ?? optionLine.underOption?.optionGroupId)?.toString() ?? '',
            isOpen: !isStatisticsOpen,
            tracking: isStatisticsOpen ? false : undefined,
        });
    }

    private getPlayerName(option: EventOption): string {
        const marketId = option.optionGroupId;
        const optionGroups = this.event.marketsDictionary[marketId];

        return (optionGroups && optionGroups.length && optionGroups[0].player1) || '';
    }

    private getOptionLines(optionGroup: EventOptionGroup): IPlayerPropsOptionLine[] {
        const orderByOptionAttribute = this.marketGroupingConfig.orderByLineValue && optionGroup.detailedGrouping.orderType === OrderType.LineValue;

        const optionGroups = this.optionGroupService.getOptionGroups(
            optionGroup,
            (option) => option.optionGroupId,
            orderByOptionAttribute ? (o) => o.optionAttribute : undefined,
            orderByOptionAttribute ? SortOrder.Descending : undefined,
        );

        const options = optionGroups.flatMap((group) => group.options);
        const hasTotalPrefix = options.every((o) => !!o.totalsPrefix);
        let optionLines;
        if (hasTotalPrefix) {
            optionLines = optionGroups.reduce<IPlayerPropsOptionLine[]>((previous, current, index) => {
                const overOption = optionGroups[index].options.find((option) => option.totalsPrefix === TotalsPrefix.Over);
                const underOption = optionGroups[index].options.find((option) => option.totalsPrefix === TotalsPrefix.Under);
                previous.push({
                    playerId: optionGroups[index].options[0].player1Id,
                    playerName: this.getPlayerName(optionGroups[index].options[0]),
                    overOption,
                    underOption,
                });

                return previous;
            }, []);
        } else {
            optionLines = options.reduce<IPlayerPropsOptionLine[]>((previous, current, index) => {
                if (index % 2 === 0) {
                    const overOption = options[index];
                    const underOption = options[index + 1];
                    previous.push({
                        playerId: options[index].player1Id,
                        playerName: this.getPlayerName(options[index]),
                        overOption,
                        underOption,
                    });
                }

                return previous;
            }, []);
        }
        return optionLines;
    }

    private setProperties(): void {
        this.isTeamGroupingEnabled = this.optionGroupService.isTeamGroupingEnabled(
            this.event,
            this.optionGroup,
            this._teamGroupingTabVisibilityThreshold,
        );
    }

    private fetchPlayerStatistics(): void {
        this.isSeasonStatisticsEnabled.set(this.playerStatisticsService.isSeasonStatisticsVisible(this.event.league.id, OptionGroupType.PlayerProps));
        this.isPlayerStatisticsEnabled = this.playerStatisticsService.isPlayerStatisticsVisible(this.event.league.id, OptionGroupType.PlayerProps);

        if (this.isPlayerStatisticsEnabled || this.isSeasonStatisticsEnabled()) {
            this.store.dispatch(
                loadPlayerStatisticsData({
                    eventId: this.event.id,
                    marketFilter: this.optionGroup.originalId,
                    mainMarket: false,
                }),
            );
            this.onPlayerStatisticsStoreUpdate(this.event.id, this.optionGroup.originalId);
        }
    }

    private onPlayerStatisticsStoreUpdate(eventId: string, marketFilter: string) {
        const key = `${eventId}_${marketFilter}`;

        const existingSubscription = this.dataSubscriptions.get(key);
        if (existingSubscription) {
            existingSubscription.unsubscribe();
        }

        const subscription = this.store
            .select(selectDataByEventAndMarket(eventId, marketFilter))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data: PlayerStatisticsStoreData) => {
                if (data) {
                    this.playerStatistics.set({
                        isLoading: data.isLoading,
                        isError: data.isError,
                        data: data.data,
                    });
                }
            });

        this.dataSubscriptions.set(key, subscription);
    }
}
